/**
 * 1.6.0 the PRISM MCP connector's install instructions as a block (stream T): the OS, the download with its size and
 * SHA-256, the steps (lib/mcp-install.ts), the program's path and the client snippets, and the update command. Shown
 * by Administration → Integrations (every OS) and My account → AI assistant access (the person's own OS first, and
 * with the token just made, when there is one).
 */
import { useState } from 'react';
import { Copy, Download } from 'lucide-react';
import { useMcpDownloads } from '../../../api/hooks';
import { copy } from '../../../lib/clipboard';
import {
  MCP_OSES, MCP_OS_LABELS, claudeDesktopConfigPath, detectMcpOs, mcpClientSnippets, mcpFileName, mcpInstallSteps, mcpOsOf, mcpProgramPath, mcpSize, mcpUpdateCommand, type McpOs,
} from '../../../lib/mcp-install';

/** A command in its own block with its own copy button. */
export function McpCommand({ id, command, label = 'Copy' }: { id: string; command: string; label?: string }) {
  return (
    <div className="mcp-command">
      <pre className="code-block" data-testid={`mcp-${id}`}>{command}</pre>
      <button type="button" className="btn sm" style={{ alignSelf: 'flex-start' }} data-testid={`mcp-copy-${id}`} onClick={() => copy(command)}><Copy size={13} /> {label}</button>
    </div>
  );
}

export function McpInstall({ token, testId = 'mcp-install' }: { token?: string; testId?: string }) {
  const downloads = useMcpDownloads();
  const [os, setOs] = useState<McpOs>(() => detectMcpOs() ?? 'darwin-arm64');
  const [path, setPath] = useState<string>('');
  const origin = window.location.origin;
  const data = downloads.data ?? null;
  const file = data?.files.find((f) => mcpOsOf(f) === os) ?? null;
  const steps = data ? mcpInstallSteps(os, { origin, version: data.version, token }) : [];
  const programPath = path || mcpProgramPath(os);
  const snippets = mcpClientSnippets(programPath);
  return (
    <div className="mcp-install" data-testid={testId}>
      <div className="field">
        <label htmlFor={`${testId}-os`}>Computer</label>
        <select id={`${testId}-os`} className="select" style={{ maxWidth: 280 }} value={os} onChange={(e) => { setOs(e.target.value as McpOs); setPath(''); }} data-testid="mcp-os">
          {MCP_OSES.map((o) => <option key={o} value={o}>{MCP_OS_LABELS[o]}</option>)}
        </select>
      </div>
      {downloads.isLoading ? <div className="skeleton" style={{ height: 80 }} /> : !data ? (
        <div className="banner" data-testid="mcp-no-downloads"><div className="banner-body"><strong>This PRISM has no connector downloads</strong>
          <span>The web image is built with them by default (PRISM_MCP_BINARIES=1); in development, build them with <code>npm run build:mcp</code>.</span></div></div>
      ) : (
        <>
          <p className="mcp-file faint" data-testid="mcp-file">
            {file ? <>
              <a className="btn sm ghost" href={`${data.base}/${file.file}`} download><Download size={13} /> {file.file}</a>
              {' '}Version {data.version} · {mcpSize(file.size)} · SHA-256 <code data-testid="mcp-sha">{file.sha256}</code>
            </> : <>Version {data.version} has no {MCP_OS_LABELS[os]} build ({mcpFileName(os)}).</>}
          </p>
          <ol className="mcp-steps">
            {steps.map((s, i) => (
              <li key={s.title}><strong>{s.title}</strong><McpCommand id={`step-${i}`} command={s.command} /></li>
            ))}
          </ol>
        </>
      )}
      <p className="section-label" style={{ marginTop: 12 }}>Add it to your AI assistant</p>
      <div className="field">
        <label htmlFor={`${testId}-path`}>The program’s full path (the last step prints it)</label>
        <input id={`${testId}-path`} className="input mono" value={path} placeholder={mcpProgramPath(os)} onChange={(e) => setPath(e.target.value)} data-testid="mcp-path" />
      </div>
      {snippets.map((s) => (
        <div key={s.client} className="mcp-snippet" data-testid={`mcp-snippet-${s.client}`}>
          <strong>{s.title}</strong>
          {s.client === 'claude-desktop' && <span className="faint" style={{ display: 'block', fontSize: 12 }}>In {claudeDesktopConfigPath(os)} (Settings → Developer → Edit Config), then restart Claude Desktop. Keep any other servers already listed.</span>}
          {s.client === 'claude-code' && <span className="faint" style={{ display: 'block', fontSize: 12 }}>Run once in a terminal; add <code>--scope user</code> to use it in every project.</span>}
          <McpCommand id={`client-${s.client}`} command={s.text} />
        </div>
      ))}
      <p className="section-label" style={{ marginTop: 12 }}>Updating</p>
      <p className="muted" style={{ margin: 0 }}>The connector downloads this PRISM’s version, checks its signature and replaces itself:</p>
      <McpCommand id="update" command={mcpUpdateCommand(os)} />
    </div>
  );
}
